import { Trans } from "@onefront/react-sdk";
import { useWsPatch, useWsPost } from "@onefront/workspace-forms";
import { Box, Stack, Step, StepLabel, Stepper } from "@vapor/react-material";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { IntermediaryData } from "../../../../../../../@types/dtos/intermediaries/IntermediaryData";
import { IntermediaryListDto } from "../../../../../../../@types/dtos/intermediaries/IntermediaryListDto";
import { IntermediarySections } from "../../../../../../../@types/enums/intermediaries/IntermediariesSections";
import WsSnackbar from "../../../../../../../components/ws-snackbar";
import { usePostDelegateds } from "../../../../../../../hooks/api/intermediaries/usePostDelegateds";
import { usePostIntermediaryData } from "../../../../../../../hooks/api/intermediaries/usePostIntermediaryData";
import { usePostSecurityEnvironments } from "../../../../../../../hooks/api/intermediaries/usePostSecurityEnvironments";
import { usePostWorkspaceIntermediary } from "../../../../../../../hooks/api/intermediaries/usePostWorkspaceIntermediary";
import { IntermediaryDataForm } from "./IntermediaryDataForm";
import { IntermediaryDelegatedForm } from "./IntermediaryDelegatedForm";
import { IntermediarySecurityEnvForm } from "./IntermediarySecurityEnvForm";
import { IntermediaryTelematicDataForm } from "./IntermediaryTelematicDataForm";

export const IntermediarySection = {
    [IntermediarySections.data]: 0,
    [IntermediarySections.telematicData]: 1,
    [IntermediarySections.securityenv]: 2,
    [IntermediarySections.delegated]: 3
};

export const IntermediarySteps = ({ step = "data", mode = "create" }) => {
    const stepIndex = IntermediarySection[step];
    const intermediarySteps = Object.values(IntermediarySections);

    return (
        <Box sx={{ width: "550px" }}>
            <Stepper activeStep={stepIndex} alternativeLabel>
                {intermediarySteps.map(step => {
                    if (step) {
                        return (
                            <Step key={`step.business.${step}`}>
                                <StepLabel>
                                    <Trans i18nKey={`step.intermediary.${step}`} />
                                </StepLabel>
                            </Step>
                        );
                    }
                    return null;
                })}
            </Stepper>
        </Box>
    );
};

interface IIntermediaryStepForm {
    step: any;
    mode: any;
    tenantItemId: any;
    linkToWorkspaceId?: any;
    intermediary?: IntermediaryListDto;
    loading?: boolean;
    onTaxId?: (taxId: string) => void;
    getIntermediary?: () => void;
    intermediaries?: Array<IntermediaryListDto>;
}

export const IntermediaryStepForm = ({
    step = "data",
    mode = "CREATE",
    tenantItemId,
    linkToWorkspaceId,
    intermediary,
    loading,
    onTaxId,
    intermediaries,
    getIntermediary
}: IIntermediaryStepForm) => {
    const [searchParams] = useSearchParams();
    const backToWorkspace = searchParams.get("backToWorkspace");
    const navigate = useNavigate();
    const [activeStep, setActiveStep] = useState<string>(step);
    const intermediaryTaxId = useParams().intermediaryTaxId;
    const [taxId, setTaxId] = useState<string | undefined>(intermediaryTaxId ?? "");
    const [feedback, setFeedback] = useState<any>(null);

    const { fetch: postIntermediaryData, loading: postIntermediaryDataLoading } = usePostIntermediaryData(tenantItemId);
    const { fetch: patchIntermediaryData, loading: patchIntermediaryDataLoading } = useWsPatch("", {});
    const { fetch: postWorkspaceIntermediary, loading: postWorkspaceIntermediaryLoading } =
        usePostWorkspaceIntermediary(tenantItemId, linkToWorkspaceId);
    const { fetch: postSecurityEnvs, loading: postSecurityEnvsLoading } = usePostSecurityEnvironments(tenantItemId);
    const { fetch: postDelegateds, loading: postDelegatedsLoading } = usePostDelegateds(tenantItemId, taxId);
    const { fetch: patchDelegateds, loading: patchDelegatedsLoading } = useWsPatch("", {});
    const { fetch: postTelematicData, loading: postTelematicDataLoading } = useWsPost("", {});
    const { fetch: patchTelematicData, loading: patchTelematicDataLoading } = useWsPatch("", {});

    useEffect(() => {
        setActiveStep(step);
    }, [mode, step]);

    useEffect(() => {
        if (!intermediaryTaxId) return;
        setTaxId(intermediaryTaxId);
    }, [intermediaryTaxId]);

    const handleSaveIntermediaryData = (data: IntermediaryData) => {
        const { type, ...rest } = data;
        const {
            defaultSignerTaxId: defSignerTaxId,
            vatNumber: exVatNumber,
            taxId: exTaxId,
            description: patchDescription,
            name: patchName,
            surname: patchSurname,
            ...restPatch
        } = rest;
        const dataPatch =
            type === "C"
                ? { ...restPatch, description: patchDescription }
                : { ...restPatch, name: patchName, surname: patchSurname };
        setTaxId(data?.taxId);
        if (mode === "CREATE") {
            postIntermediaryData({
                data: {
                    ...rest
                }
            })
                .then(() => {
                    if (linkToWorkspaceId && mode === "CREATE") {
                        postWorkspaceIntermediary?.({
                            data: {
                                intermediaryId: data?.taxId
                            }
                        });
                    }

                    setTaxId(data?.taxId);
                    onTaxId?.(data?.taxId);

                    setActiveStep(IntermediarySections.telematicData);
                })
                .catch(err => {
                    setFeedback({
                        type: "error",
                        message: "Errore nella creazione dell'intermediario"
                    });
                });
        } else {
            patchIntermediaryData({
                url: `rnd:core:intermediary-registry-write:api://v1/${tenantItemId}/intermediary/${intermediaryTaxId}`,
                data: {
                    ...dataPatch
                }
            })
                .then(() => {
                    getIntermediary?.();
                    setFeedback({
                        type: "success",
                        message: "Dati salvati con successo"
                    });
                })
                .catch(err => {
                    setFeedback({
                        type: "error",
                        message: "Errore nella creazione dell'intermediario"
                    });
                });
        }
    };

    const handleTelematicPost = async (data: any) => {
        const { id, ...rest } = data;

        if (mode === "CREATE") {
            await postTelematicData({
                url: `rnd:core:workspace-bff:api://v1/intermediaries/${taxId}/telematic`,
                data: {
                    ...rest
                },
                params: {
                    itemId: tenantItemId
                }
            }).catch(err => {
                setFeedback({
                    type: "error",
                    message: "Errore nel salvataggio dei dati telematici"
                });
            });

            setActiveStep(IntermediarySections.securityenv);
        } else {
            await patchTelematicData({
                url: `rnd:core:workspace-bff:api://v1/intermediaries/${taxId}/telematic`,
                data: {
                    ...rest
                },
                params: {
                    itemId: tenantItemId
                }
            })
                .then(() => {
                    getIntermediary?.();
                    setFeedback({
                        type: "success",
                        message: "Dati salvati con successo"
                    });
                })
                .catch(err => {
                    setFeedback({
                        type: "error",
                        message: "Errore nel salvataggio dei dati telematici"
                    });
                });
        }
    };

    const handleSecurityEnvStep = (data: any) => {
        return new Promise<any>(async (resolve, reject) => {
            try {
                await patchIntermediaryData({
                    url: `rnd:core:intermediary-registry-write:api://v1/${tenantItemId}/intermediary/${taxId}`,
                    data: {
                        defaultSignerTaxId: data.defaultSignerTaxId
                    }
                });

                if (mode === "EDIT") {
                    setFeedback({
                        type: "success",
                        message: "Firmatario salvato con successo"
                    });
                }

                if (data?.securityEnvironments) {
                    const securityEnvPromises = data?.securityEnvironments?.map(async env => {
                        const { id, siteCode, expirationDate, files, password, ...rest } = env;

                        const filesMapped = files?.map(file => {
                            const { size, ...rest } = file;
                            return rest;
                        });

                        const restAttachment =
                            filesMapped?.length > 0
                                ? {
                                      securityEnvironmentAttachment: {
                                          password,
                                          securityEnvironmentFiles: filesMapped
                                      }
                                  }
                                : {};

                        await postSecurityEnvs({
                            url: `rnd:core:intermediary-registry-write:api://v1/${tenantItemId}/intermediary/${taxId}/security-environment/${siteCode}`,
                            data: {
                                ...rest,
                                ...restAttachment
                            }
                        });
                    });

                    await Promise.all(securityEnvPromises);
                }

                if (mode === "EDIT") {
                    getIntermediary?.();
                } else {
                    setActiveStep(IntermediarySections.delegated);
                }

                resolve({
                    type: "success"
                });
            } catch (err) {
                setFeedback({
                    type: "error",
                    message: "Errore nel salvataggio del certificato di firma"
                });
                reject(err);
            }
        });
    };

    const handleDelegatedPost = async (data: any, handle: boolean) => {
        if (handle) {
            const promises = data?.map(async env => {
                const { id, ...rest } = env;

                await postDelegateds({
                    url: `rnd:core:intermediary-registry-write:api://v1/${tenantItemId}/intermediary/${taxId}/delegated`,
                    data: {
                        ...rest
                    }
                });
            });

            await Promise.all(promises).catch(err => {
                setFeedback({
                    type: "error",
                    message: "Errore nel salvataggio degli incaricati"
                });
            });

            navigate(
                linkToWorkspaceId
                    ? `/details/${linkToWorkspaceId}/intermediaries`
                    : `/tenant/settings/intermediaries${backToWorkspace ? `?backToWorkspace=${backToWorkspace}` : ""}`
            );
        } else {
            navigate(
                linkToWorkspaceId
                    ? `/details/${linkToWorkspaceId}/intermediaries`
                    : `/tenant/settings/intermediaries${backToWorkspace ? `?backToWorkspace=${backToWorkspace}` : ""}`
            );
        }
    };

    const handleDelegatedPatch = async (data: any) => {
        const {
            id,
            areCredentialsStored,
            intermediaryTaxId,
            itemId,
            taxId: delegatedTaxId,
            entratelCode,
            ...dataP
        } = data;

        if (id === "-1") {
            return postDelegateds({
                url: `rnd:core:intermediary-registry-write:api://v1/${tenantItemId}/intermediary/${taxId}/delegated`,
                data: {
                    ...dataP,
                    taxId: delegatedTaxId
                }
            })
                .then(res => {
                    window.scroll(0, 0);
                    setFeedback({
                        type: "success",
                        message: "Incaricato creato con successo"
                    });

                    getIntermediary?.();
                })
                .catch(err => {
                    window.scroll(0, 0);
                    setFeedback({
                        type: "error",
                        message: "Errore nel salvataggio degli incaricati"
                    });
                });
        } else {
            return patchDelegateds({
                url: `rnd:core:intermediary-registry-write:api://v1/${tenantItemId}/intermediary/${taxId}/delegated/${delegatedTaxId}`,
                data: {
                    ...dataP
                }
            })
                .then(res => {
                    window.scroll(0, 0);
                    setFeedback({
                        type: "success",
                        message: "Incaricato salvato con successo"
                    });
                    getIntermediary?.();
                })
                .catch(err => {
                    window.scroll(0, 0);
                    setFeedback({
                        type: "error",
                        message: "Errore nel salvataggio degli incaricati"
                    });
                });
        }
    };

    const handlePreviousStep = () => {
        const workspacePath = linkToWorkspaceId
            ? `/details/${linkToWorkspaceId}/intermediaries`
            : "/tenant/settings/intermediaries";
        const backToWorkspaceQuery = backToWorkspace ? `?backToWorkspace=${backToWorkspace}` : "";
        navigate(`${workspacePath}${backToWorkspaceQuery}`);
    };

    return (
        <Stack spacing={4}>
            {mode === "CREATE" && <IntermediarySteps mode={mode} step={activeStep} />}
            <Stack spacing={3} direction="column" py={3} position={"relative"}>
                {feedback && (
                    <WsSnackbar
                        position={"absolute"}
                        sx={{
                            right: "0 !important",
                            ...(mode === "CREATE" ? { top: "-6.5rem !important" } : { top: "0 !important" }),
                            width: 700
                        }}
                        type={feedback?.type}
                        message={feedback?.message}
                        horizontal="right"
                        handleClose={() => setFeedback(null)}
                    />
                )}
                {activeStep === IntermediarySections.data && (
                    <IntermediaryDataForm
                        onSubmit={handleSaveIntermediaryData}
                        onBack={handlePreviousStep}
                        loadingGet={loading}
                        loadingPost={
                            postIntermediaryDataLoading ||
                            postWorkspaceIntermediaryLoading ||
                            patchIntermediaryDataLoading
                        }
                        mode={mode}
                        data={intermediary}
                        intermediaries={intermediaries}
                    />
                )}
                {activeStep === IntermediarySections.telematicData && (
                    <IntermediaryTelematicDataForm
                        onSubmit={handleTelematicPost}
                        onBack={handlePreviousStep}
                        loadingGet={loading}
                        loadingPost={postTelematicDataLoading || patchTelematicDataLoading}
                        mode={mode}
                        data={intermediary}
                        taxId={taxId}
                    />
                )}
                {activeStep === IntermediarySections.securityenv && (
                    <IntermediarySecurityEnvForm
                        onSubmit={handleSecurityEnvStep}
                        onBack={handlePreviousStep}
                        loadingPost={postSecurityEnvsLoading}
                        loadingPatch={patchIntermediaryDataLoading}
                        data={intermediary}
                        intermediaryTaxId={taxId}
                        mode={mode}
                        getIntermediary={getIntermediary}
                    />
                )}
                {activeStep === IntermediarySections.delegated && (
                    <IntermediaryDelegatedForm
                        onSubmit={handleDelegatedPost}
                        onPatch={handleDelegatedPatch}
                        onBack={handlePreviousStep}
                        loadingPost={postDelegatedsLoading}
                        loadingPatch={patchDelegatedsLoading}
                        data={intermediary}
                        mode={mode}
                    />
                )}
            </Stack>
            <Box height={20} />
        </Stack>
    );
};
